import {
  Button,
  Tooltip,
  Card,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  ChevronDownFilled,
  ChevronRightFilled,
  CaretUpFilled,
  CaretDownFilled,
} from '@fluentui/react-icons';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

import { Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';
import { ScreenSizes, useScreenSize } from '@tdp/common/shared';

import { analyticsDashboardStyles } from './AnalyticsDashboard.styles';
import { getAnalyticsDashboardFormattedString } from './utils';
import {
  IMetric,
  IMetricData,
  IMetricGroup,
} from '../metricsDefinition/IMetricData';

interface ITableHeader {
  name: string;
}

interface IAnalyticsDashboard {
  data?: IMetricData;
}

interface IRowItem {
  key: string;
  content: JSX.Element;
  style?: object;
}
interface IRowContent {
  key: number;
  items: IRowItem[];
  onClick?: () => void;
}

const TableColumnTitle: React.FC<ITableHeader> = ({ name }) => (
  <StyledText size="medium" weight="bold">
    {name}
  </StyledText>
);

export const AnalyticsDashboard: React.FC<IAnalyticsDashboard> = ({ data }) => {
  const intl = useIntl();
  const smallScreen: boolean = useScreenSize(ScreenSizes.Small);
  const themeStyle = analyticsDashboardStyles(smallScreen);
  const formattedStrings = getAnalyticsDashboardFormattedString(intl);

  const [metricGroupsCollapsed, setMetricGroupsCollapsed] = useState<
    Record<string, boolean>
  >({});
  const [dateIndex, setDateIndex] = useState<number>(0);

  const dataExists = !isEmpty(data?.dates);

  // Build dashboard headers
  const dashboardHeaders = {
    key: 'header',
    items: [
      {
        key: 'metricName',
        content: (
          <TableColumnTitle
            name={intl.formatMessage({
              defaultMessage: 'Metric name',
              description:
                'String displayed as the header for the table that contains the metric and its values',
            })}
          />
        ),
      },
      {
        key: 'metricValue',
        content: <TableColumnTitle name="" />,
      },
      {
        key: 'momGrowth',
        content: (
          <TableColumnTitle
            name={intl.formatMessage({
              defaultMessage: 'MoM growth',
              description:
                'Month over Month Growth. String displayed as the header for the table that contains the metric and its values',
            })}
          />
        ),
      },
    ],
  };

  // Build dashboard rows based on data
  const dashboardRows: IRowContent[] = [];
  let maxDate: Date = new Date();
  let minDate: Date = new Date();
  const dates: Date[] = [];
  const dateMaps: Record<string, number> = {};
  const restrictedDates: string[] = [];

  const buildMoMTableCell = (value: number) => {
    if (!value) {
      return (
        <Flex>
          <StyledText size="small">-</StyledText>
        </Flex>
      );
    } else if (value > 0) {
      return (
        <Flex style={themeStyle.momPositive}>
          <CaretUpFilled fontSize={20} />

          <StyledText size="small">{value}%</StyledText>
        </Flex>
      );
    } else {
      return (
        <Flex style={themeStyle.momNegative}>
          <CaretDownFilled fontSize={20} />
          <StyledText size="small">{-value}%</StyledText>
        </Flex>
      );
    }
  };

  const onMetricButtonClick = (metric: IMetric) => {
    // TODO: (astripa) Add telemetry
    // Logger.LogInfo(TelemetryObjectId.AnalyticsMetricDialogButton, TelemetryEventName.Click, metric.name);
  };

  let key = 1;
  if (dataExists) {
    // convert dates and populate date maps
    data?.dates.forEach((dateString: string, index: number) => {
      const utcDate = new Date(dateString);
      const localDate = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate()
      );

      dates.push(localDate);
      dateMaps[localDate.toISOString()] = index;
    });

    // Find max and min date
    maxDate = new Date(dates[0]);
    minDate = new Date(dates[dates.length - 1]);

    // List of unavailable dateso
    const curDate = new Date(minDate);
    while (curDate < maxDate) {
      const currentDateISOString = curDate.toISOString();
      if (!(currentDateISOString in dateMaps)) {
        restrictedDates.push(currentDateISOString);
      }

      curDate.setDate(curDate.getDate() + 1);
    }

    const date = data?.dates[dateIndex];
    data?.metricGroups.forEach((metricGroup: IMetricGroup) => {
      const metrics = metricGroup.metrics;
      const isCollapsed = metricGroupsCollapsed[metricGroup.name];
      if (metrics) {
        dashboardRows.push({
          key: key++,
          items: [
            {
              key: 'metricGroupName',
              content: (
                <Button
                  appearance="transparent"
                  style={themeStyle.metricGroupButton}
                  icon={
                    isCollapsed ? (
                      <ChevronRightFilled fontSize={20} />
                    ) : (
                      <ChevronDownFilled fontSize={20} />
                    )
                  }
                  iconPosition="before"
                >
                  {getFormattedContent(
                    metricGroup.nameStringKey,
                    metricGroup.name,
                    formattedStrings,
                    intl
                  )}
                </Button>
              ),
            },
          ],
          onClick: () => {
            setMetricGroupsCollapsed({
              ...metricGroupsCollapsed,
              [metricGroup.name]: !isCollapsed,
            });
          },
        });

        if (!isCollapsed) {
          metrics.forEach((metric: IMetric) => {
            const metricNameButton = (
              <Button
                style={{
                  ...themeStyle.metricButton,
                  width: '100%',
                  flexGrow: 1,
                }}
                appearance="transparent"
                size="small"
              >
                {getFormattedContent(
                  metric.nameStringKey,
                  metric.name,
                  formattedStrings,
                  intl
                )}
              </Button>
            );

            dashboardRows.push({
              key: key++,
              items: [
                {
                  key: 'metricName',
                  content: metric.tooltipTextStringKey ? (
                    <Tooltip
                      relationship="label"
                      content={getFormattedContent(
                        metric.tooltipTextStringKey,
                        metric.name,
                        formattedStrings,
                        intl
                      )}
                    >
                      {metricNameButton}
                    </Tooltip>
                  ) : (
                    metricNameButton
                  ),
                },
                {
                  key: 'metricValue',
                  content: (
                    <Button
                      style={{
                        ...themeStyle.valueButton,
                        width: '100%',
                        flexGrow: 1,
                      }}
                      appearance="transparent"
                      size="small"
                    >
                      {metric.stringValues[date ?? ''] ?? '-'}
                    </Button>
                  ),
                  style: themeStyle.cell,
                },
                {
                  key: 'momGrowth',
                  content: buildMoMTableCell(metric.moMValues[date ?? '']),
                },
              ],
              onClick: () => {
                onMetricButtonClick(metric);
              },
            });
          });
        }
      }
    });
  }

  const TableComponent: React.FC = () => {
    return (
      <Table arial-label="Default table" style={{ minWidth: '510px', margin: "20px" }}>
        <TableHeader>
          <TableRow>
            {dashboardHeaders.items.map((header) => (
              <TableHeaderCell key={header.key}>
                {header.content}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {dashboardRows.map((item) => (
            <TableRow key={item.key} onClick={item.onClick}>
              {item.items.map((rowItem) => {
                if (rowItem.key === 'metricGroupName') {
                  return (
                    <TableCell colSpan={3} key={rowItem.key}>
                      {rowItem.content}
                    </TableCell>
                  );
                }
                return (
                  <TableCell key={rowItem.key}>{rowItem.content}</TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  const onDateSelected = (date: Date | null | undefined) => {
    if (date) {
      const dateString = date.toISOString();
      // avoid selecting restricted dates
      if (dateString in restrictedDates) {
        return;
      }
      setDateIndex(dateMaps[date.toISOString()]);
    }
    // TODO: (astripa) Add telemetry
    // Logger.LogInfo(TelemetryObjectId.AnalyticsDatePickerButton, TelemetryEventName.Click, dateString);
  };

  return (
    <Flex>
      <Card appearance="subtle" style={themeStyle.card}>
        <Flex vAlign="center" gap="gap.small" padding="padding.medium" hAlign='end'>
          <DatePicker
            minDate={minDate}
            maxDate={maxDate}
            value={maxDate}
            onSelectDate={onDateSelected}
            isMonthPickerVisible={false}
          />
        </Flex>
        <TableComponent />
      </Card>
    </Flex>
  );
};

const getFormattedContent = (
  metricKey: string,
  metricName: string,
  formattedStrings: ReturnType<typeof getAnalyticsDashboardFormattedString>,
  intl: IntlShape
) => {
  return metricKey in formattedStrings
    ? formattedStrings[metricKey as keyof typeof formattedStrings]
    : intl.formatMessage({ id: metricKey, defaultMessage: metricName });
};
